/**
 * Text Merging + Normalization + 16-Format Classification Cascade
 *
 * Production-validated against 18 real-world PV site layout PDFs.
 *
 * Pipeline:
 *   1. Merge adjacent text items (fragmented labels)
 *   2. Normalize: collapse intra-token spaces, strip trailing '/'
 *   3. Extract parenthetical metadata: (27), (H), MPPT1
 *   4. Classify via cascading regex (user custom → built-in hierarchical → standalone → infra)
 */

import type {
  ClassificationPattern,
  LayoutClassification,
  ClassificationMetadata,
  LayoutTextEntity,
  TopologyLevel,
  TopologyFields,
} from './types';

/**
 * The classification that GOVERNS an entity: manual override beats auto.
 * Every consumer MUST read through this — reading `.classification` directly
 * reintroduces the membership/fields split-brain (an entity selected by one
 * source with its hierarchy read from the other).
 */
export function effectiveClassification(
  e: LayoutTextEntity,
): LayoutClassification | undefined {
  return e.classificationOverride ?? e.classification;
}

// ── Raw text item from PDF extraction ─────────────────────

export interface RawTextItem {
  str: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  rotation: number;
}

// ── Hebrew filter ─────────────────────────────────────────

const HEBREW_RE = /[\u0590-\u05FF]/;

export function containsHebrew(text: string): boolean {
  return HEBREW_RE.test(text);
}

// ── Infrastructure skip list ──────────────────────────────

const INFRASTRUCTURE_WORDS = new Set([
  'SCADA', 'UPS', 'AUX', 'LVP', 'RMU', 'LVDB', 'LVAC',
  'MV', 'LV', 'HV', 'AC', 'DC',
  'LEGEND', 'TITLE', 'NOTE', 'NOTES', 'REV', 'REVISION',
  'COMMUNICATION', 'COMM', 'CABLE', 'TRAY', 'TRENCH',
  'FENCE', 'GATE', 'ROAD', 'ACCESS', 'PARKING',
  'TRANSFORMER', 'XFMR', 'SWITCHGEAR', 'PANEL',
  'METER', 'MONITORING', 'WEATHER', 'PYRANOMETER',
  'DETAIL', 'SECTION', 'ELEVATION', 'PLAN', 'VIEW',
  'SCALE', 'DATE', 'DWG', 'SHEET', 'PAGE',
  'NORTH', 'SOUTH', 'EAST', 'WEST',
  'SOLAREDGE', 'HUAWEI', 'SMA', 'ABB', 'SCHNEIDER',
  'GENERAL', 'TYPICAL', 'STANDARD', 'SPECIFICATION',
]);

function isInfrastructureLabel(text: string): boolean {
  const upper = text.toUpperCase().trim();
  // Direct match
  if (INFRASTRUCTURE_WORDS.has(upper)) return true;
  // Check if any infrastructure word is the primary content
  for (const word of INFRASTRUCTURE_WORDS) {
    if (upper === word || upper.startsWith(word + ' ') || upper.startsWith(word + '-')) {
      return true;
    }
  }
  return false;
}

// ── Text Item Merging ─────────────────────────────────────

const MAX_MERGE_CHAIN = 5;

/**
 * Merge adjacent PDF text items that form a single logical label.
 * Handles fragmentation like "1.1.DCCB05." + "STR03".
 *
 * Criteria for merging:
 *   - First item ends with '.' (dot-continuation)
 *   - Y positions within ±fontSize threshold
 *   - X gap < fontSize * 3
 *   - Max chain length = 5
 */
export function mergeAdjacentTextItems(items: RawTextItem[]): RawTextItem[] {
  if (items.length <= 1) return items;

  // Sort by Y (top-to-bottom within rotation tolerance), then X (left-to-right)
  const sorted = [...items].sort((a, b) => {
    const yDiff = Math.abs(a.y - b.y);
    const threshold = Math.min(a.fontSize, b.fontSize) * 0.5;
    if (yDiff > threshold) return a.y - b.y;
    return a.x - b.x;
  });

  const result: RawTextItem[] = [];
  let i = 0;

  while (i < sorted.length) {
    const current = { ...sorted[i] };
    let chainLength = 1;

    // Try to merge subsequent items
    while (i + 1 < sorted.length && chainLength < MAX_MERGE_CHAIN) {
      const next = sorted[i + 1];
      const currentRight = current.x + current.width;
      const xGap = next.x - currentRight;
      const yDiff = Math.abs(current.y - next.y);
      const fSize = Math.min(current.fontSize, next.fontSize);
      const yThreshold = fSize * 0.5;
      const xThreshold = fSize * 3;

      // Merge conditions: dot-continuation OR very close adjacency
      const dotContinuation = current.str.trimEnd().endsWith('.');
      const veryClose = xGap >= 0 && xGap < fSize * 0.5;

      if (yDiff <= yThreshold && xGap >= -fSize && xGap <= xThreshold &&
          (dotContinuation || veryClose)) {
        // Merge: concatenate text, extend bbox
        current.str = current.str + next.str;
        current.width = (next.x + next.width) - current.x;
        current.height = Math.max(current.height, next.height);
        current.fontSize = Math.max(current.fontSize, next.fontSize);
        i++;
        chainLength++;
      } else {
        break;
      }
    }

    result.push(current);
    i++;
  }

  return result;
}

// ── Normalization ─────────────────────────────────────────

/**
 * Collapse spaces within tokens: "STR 0 7" → "STR07", "INV 01" → "INV01"
 * Also trims and strips trailing '/'
 */
export function normalizeLabel(text: string): string {
  let s = text.trim();
  // Strip trailing '/' or ' /'
  s = s.replace(/\s*\/\s*$/, '');
  // Collapse spaces within alphanumeric tokens
  // e.g. "TR 1 -INV 01-STR 0 7" → "TR1-INV01-STR07"
  s = s.replace(/([A-Za-z])\s+(\d)/g, '$1$2');
  s = s.replace(/(\d)\s+(\d)/g, '$1$2');
  s = s.replace(/(\d)\s+([A-Za-z])/g, '$1$2');
  // Collapse "- " or " -" to just "-"
  s = s.replace(/\s*-\s*/g, '-');
  // Collapse ". " or " ." to just "."
  s = s.replace(/\s*\.\s*/g, '.');
  // Strip trailing dot (from fragmented labels like "1.INV01." on one line)
  s = s.replace(/\.$/, '');
  return s.trim();
}

// ── Metadata Extraction ───────────────────────────────────

export interface MetadataResult {
  cleaned: string;
  metadata: ClassificationMetadata;
}

/**
 * Extract parenthetical metadata from label suffix and strip trailing noise.
 *
 * Handles all parenthetical formats seen in real-world PV layout PDFs:
 *   "(H)" → half=true
 *   "(27)" → modules=27
 *   "(4.PV18)", "(2.R)", "(5.PV20)" → stripped (PV panel info, not topology)
 *   "MPPT1" → mppt="MPPT1"
 *   "-A", "-B" → stripped (string half/section suffixes)
 */
export function extractMetadata(text: string): MetadataResult {
  let cleaned = text;
  const metadata: ClassificationMetadata = {};

  // Extract MPPT suffix: ...MPPT1 or ...MPPT01
  const mpptMatch = cleaned.match(/\s*(MPPT\d+)\s*$/i);
  if (mpptMatch) {
    metadata.mppt = mpptMatch[1].toUpperCase();
    cleaned = cleaned.slice(0, mpptMatch.index).trim();
  }

  // Strip ALL trailing parentheticals (loop handles multiple: "(H)(5.PV20)")
  // Parse known metadata from the content, discard unknown
  for (let iter = 0; iter < 5; iter++) {
    const parenMatch = cleaned.match(/\s*\(([^)]*)\)\s*$/);
    if (!parenMatch) break;

    const content = parenMatch[1].trim();
    if (/^H$/i.test(content)) {
      metadata.half = true;
    } else if (/^\d+$/.test(content)) {
      metadata.modules = parseInt(content, 10);
    }
    // Other formats like "(4.PV18)", "(2.R)" are stripped but not stored as topology data

    cleaned = cleaned.slice(0, parenMatch.index).trim();
  }

  // Strip trailing letter suffix: "-A", "-B" (string half identifiers)
  const suffixMatch = cleaned.match(/\s*-[A-Z]\s*$/i);
  if (suffixMatch) {
    cleaned = cleaned.slice(0, suffixMatch.index).trim();
  }

  return { cleaned, metadata };
}

// ── Built-in Classification Patterns ──────────────────────

interface BuiltinPattern {
  id: string;
  regex: RegExp;
  level: string;
  priority: number;
  extract: (match: RegExpMatchArray) => {
    hierarchy: Partial<TopologyFields>;
    value: string;
  };
}

const BUILTIN_PATTERNS: BuiltinPattern[] = [
  // Format 1: 2.1.DCCB03.STR06 (Station.MPPT.DCCB.STR)
  {
    id: 'fmt-01-4level',
    regex: /^(\d+)\.(\d+)\.(DCCB\d+)\.(STR\d+)$/i,
    level: 'string',
    priority: 100,
    extract: (m) => ({
      hierarchy: { station: m[1], mppt: m[2], dccb: m[3], string: m[4] },
      value: `${m[1]}.${m[2]}.${m[3]}.${m[4]}`,
    }),
  },
  // Format 5: 1.08.22.Str7 (Sec.DCCB.INV.String — 4 numeric levels)
  {
    id: 'fmt-05-sec-dccb-inv-str',
    regex: /^(\d+)\.(\d+)\.(\d+)\.(?:STR|Str)(\d+)$/i,
    level: 'string',
    priority: 101,
    extract: (m) => ({
      hierarchy: { station: m[1], dccb: m[2], inverter: m[3], string: m[4] },
      value: `${m[1]}.${m[2]}.${m[3]}.Str${m[4]}`,
    }),
  },
  // Format 7: TR1-INV01-STR07 (station-inv-str compound)
  {
    id: 'fmt-07-tr-inv-str',
    regex: /^TR(\d+)[-.]INV(\d+)[-.]STR(\d+)/i,
    level: 'string',
    priority: 102,
    extract: (m) => ({
      hierarchy: { station: `TR${m[1]}`, inverter: `INV${m[2]}`, string: `STR${m[3]}` },
      value: `TR${m[1]}-INV${m[2]}-STR${m[3]}`,
    }),
  },
  // Format 3: 1.INV01.Str01 (Section.INV.String)
  {
    id: 'fmt-03-sec-inv-str',
    regex: /^(\d+)\.(INV\d+)\.(?:STR|Str)(\d+)$/i,
    level: 'string',
    priority: 103,
    extract: (m) => ({
      hierarchy: { station: m[1], inverter: m[2], string: `STR${m[3]}` },
      value: `${m[1]}.${m[2]}.Str${m[3]}`,
    }),
  },
  // Format 4: INV1.Str1 (after metadata extraction removes (27)MPPT1)
  {
    id: 'fmt-04-inv-str',
    regex: /^(INV\d+)\.(?:STR|Str)(\d+)$/i,
    level: 'string',
    priority: 104,
    extract: (m) => ({
      hierarchy: { inverter: m[1], string: `STR${m[2]}` },
      value: `${m[1]}.Str${m[2]}`,
    }),
  },
  // Format 2: 1.DCCB01.STR09 (Section.DCCB.String)
  {
    id: 'fmt-02-sec-dccb-str',
    regex: /^(\d+)\.(DCCB\d+)\.(STR\d+)$/i,
    level: 'string',
    priority: 105,
    extract: (m) => ({
      hierarchy: { station: m[1], dccb: m[2], string: m[3] },
      value: `${m[1]}.${m[2]}.${m[3]}`,
    }),
  },
  // Format 6: 1.01.Str01 (Sec.DCCB.String with numeric DCCB)
  {
    id: 'fmt-06-sec-num-str',
    regex: /^(\d+)\.(\d+)\.(?:STR|Str)(\d+)$/i,
    level: 'string',
    priority: 106,
    extract: (m) => ({
      hierarchy: { station: m[1], dccb: m[2], string: `STR${m[3]}` },
      value: `${m[1]}.${m[2]}.Str${m[3]}`,
    }),
  },
  // Format 8: INV1-DCCB1 (INV-DCCB combined)
  {
    id: 'fmt-08-inv-dccb',
    regex: /^(INV\d+)[-.]?(DCCB\d+)$/i,
    level: 'dccb',
    priority: 107,
    extract: (m) => ({
      hierarchy: { inverter: m[1], dccb: m[2] },
      value: `${m[1]}-${m[2]}`,
    }),
  },
  // Format 9: 1.DCCB01 (Section.DCCB standalone)
  {
    id: 'fmt-09-sec-dccb',
    regex: /^(\d+)\.(DCCB\d+)$/i,
    level: 'dccb',
    priority: 108,
    extract: (m) => ({
      hierarchy: { station: m[1], dccb: m[2] },
      value: `${m[1]}.${m[2]}`,
    }),
  },
  // Format 10: 1.ACB01 (ACB ≡ DCCB)
  {
    id: 'fmt-10-sec-acb',
    regex: /^(\d+)\.(ACB\d+)$/i,
    level: 'dccb',
    priority: 109,
    extract: (m) => ({
      hierarchy: { station: m[1], dccb: m[2].replace(/^ACB/i, 'DCCB') },
      value: `${m[1]}.${m[2]}`,
    }),
  },
  // Format 11: M.19.10 (Letter.Num.Num zones)
  {
    id: 'fmt-11-zone',
    regex: /^([A-Z])\.(\d+)\.(\d+)$/,
    level: 'zone',
    priority: 110,
    extract: (m) => ({
      hierarchy: { station: `${m[1]}.${m[2]}`, string: m[3] },
      value: `${m[1]}.${m[2]}.${m[3]}`,
    }),
  },
  // Format 13: W-Row2 Column17 (direction-row-column — rare, from specific site format)
  {
    id: 'fmt-13-dir-row-col',
    regex: /^([NSEW])-?Row(\d+)\s*Column(\d+)$/i,
    level: 'string',
    priority: 111,
    extract: (m) => ({
      hierarchy: { station: `${m[1]}-Row${m[2]}`, string: `Col${m[3]}` },
      value: `${m[1]}-Row${m[2]}-Col${m[3]}`,
    }),
  },
  // Format 14: 1.INV01 (Section.Inverter — no string part, from fragmented multi-line labels)
  {
    id: 'fmt-14-sec-inv',
    regex: /^(\d+)\.(INV\d+)$/i,
    level: 'inverter',
    priority: 115,
    extract: (m) => ({
      hierarchy: { station: m[1], inverter: m[2] },
      value: `${m[1]}.${m[2]}`,
    }),
  },
  // Format 12: 1.1 (Short section.string — MUST be after all longer dot-formats)
  // Requires both parts to start with non-zero digit to avoid matching decimals (0.5, 1.0)
  {
    id: 'fmt-12-short',
    regex: /^([1-9]\d?)\.([1-9]\d{0,2})$/,
    level: 'string',
    priority: 120,
    extract: (m) => ({
      hierarchy: { station: m[1], string: m[2] },
      value: `${m[1]}.${m[2]}`,
    }),
  },
];

// Standalone label patterns (single-entity, non-hierarchical)
// Digit counts limited to 1-3 to prevent matching coordinates/dimensions
const STANDALONE_PATTERNS: Array<{
  regex: RegExp;
  level: string;
  extract: (m: RegExpMatchArray) => string;
}> = [
  // INV/Inverter labels
  { regex: /^(?:INV|INVERTER)\s*(\d{1,3})$/i, level: 'inverter', extract: (m) => `INV${m[1]}` },
  // MPPT labels
  { regex: /^(?:MPPT)\s*(\d{1,3})$/i, level: 'mppt', extract: (m) => `MPPT${m[1]}` },
  // DCCB/DCB/CB/ACB labels
  { regex: /^(?:DCCB|DCB|CB|ACB)\s*(\d{1,3})$/i, level: 'dccb', extract: (m) => `DCCB${m[1]}` },
  // STR/String labels
  { regex: /^(?:STR|STRING)\s*(\d{1,3})$/i, level: 'string', extract: (m) => `STR${m[1]}` },
  // TS/TR/Station labels (Format 16)
  { regex: /^(?:TS|TR|STATION)\s*(\d{1,3})$/i, level: 'station', extract: (m) => `TS${m[1]}` },
  // MOD/Module labels (Format 14)
  { regex: /^(?:MOD|MODULE|M)\s*(\d{1,4})$/i, level: 'module', extract: (m) => `MOD${m[1]}` },
  // Row labels (Format 15): R1, Row 1
  { regex: /^(?:R|ROW)\s*(\d{1,3})$/i, level: 'row', extract: (m) => `R${m[1]}` },
];

// ── User Pattern Compilation ──────────────────────────────

function compileUserPattern(
  pattern: ClassificationPattern,
): BuiltinPattern | null {
  try {
    const regex = new RegExp('^' + pattern.regex + '$', 'i');
    return {
      id: pattern.id,
      regex,
      level: pattern.level,
      priority: pattern.priority,
      extract: (m) => {
        const hierarchy: Partial<TopologyFields> = {};
        for (const [field, groupIdx] of Object.entries(pattern.captureGroups)) {
          if (m[groupIdx]) {
            (hierarchy as Record<string, string>)[field] = m[groupIdx];
          }
        }
        return { hierarchy, value: m[0] };
      },
    };
  } catch {
    console.warn(`[classify] Invalid user regex pattern: ${pattern.regex}`);
    return null;
  }
}

// ── Main Classification ───────────────────────────────────

/**
 * Bumped whenever classification behavior changes. Stamped on the layout at
 * (re)classification time so "stored classifications predate current code"
 * is a queryable state instead of a forensic discovery.
 */
export const CLASSIFIER_VERSION = 2;

/**
 * Classify a single normalized label text.
 * Returns null if unclassified (Hebrew, infrastructure, no pattern match).
 */
export function classifyLabel(
  normalizedText: string,
  customPatterns?: ClassificationPattern[],
): LayoutClassification | null {
  if (!normalizedText) return null;
  if (containsHebrew(normalizedText)) return null;

  // Single-character labels carry no signal for the generic cascade — only a
  // user-defined pattern may claim them (bare in-table string numbers 1-9).
  const tooShort = normalizedText.length < 2;

  if (!tooShort && isInfrastructureLabel(normalizedText)) {
    return { level: 'infrastructure', value: normalizedText };
  }

  // Extract metadata first (removes parenthetical suffixes)
  const { cleaned, metadata } = extractMetadata(normalizedText);
  const hasMetadata = Object.keys(metadata).length > 0;

  const finalize = (
    pattern: BuiltinPattern,
    match: RegExpMatchArray,
  ): LayoutClassification => {
    const { hierarchy, value } = pattern.extract(match);
    const result: LayoutClassification = {
      level: pattern.level,
      value,
      hierarchy,
    };

    if (hasMetadata) {
      // For DCCB-level labels, numeric parenthetical is stringCount, not modules
      if (pattern.level === 'dccb' && metadata.modules != null) {
        metadata.stringCount = metadata.modules;
        delete metadata.modules;
      }
      result.metadata = metadata;
    }

    return result;
  };

  // User patterns first (sorted by priority). Each is tried against the
  // metadata-stripped form AND the full normalized text — schemas built from
  // samples that include parentheticals (e.g. "1.1.DCCB08.STR14(H)") compile
  // to regexes containing the parenthetical, which only the full text can match.
  if (customPatterns?.length) {
    const compiled = customPatterns
      .map(compileUserPattern)
      .filter((p): p is BuiltinPattern => p !== null)
      .sort((a, b) => a.priority - b.priority);
    for (const pattern of compiled) {
      const match =
        cleaned.match(pattern.regex) ?? normalizedText.match(pattern.regex);
      if (match) return finalize(pattern, match);
    }
  }

  if (tooShort) return null;

  // Try built-in hierarchical patterns (first match wins)
  for (const pattern of BUILTIN_PATTERNS) {
    const match = cleaned.match(pattern.regex);
    if (match) return finalize(pattern, match);
  }

  // Try standalone patterns
  for (const pattern of STANDALONE_PATTERNS) {
    const match = cleaned.match(pattern.regex);
    if (match) {
      const result: LayoutClassification = {
        level: pattern.level,
        value: pattern.extract(match),
      };
      if (hasMetadata) result.metadata = metadata;
      return result;
    }
  }

  // Keyword-based fallback: handles novel combinations of known keywords
  const fallbackResult = classifyByKeywordFallback(cleaned);
  if (fallbackResult) {
    // Same DCCB stringCount logic as above
    if (fallbackResult.level === 'dccb' && metadata.modules != null) {
      metadata.stringCount = metadata.modules;
      delete metadata.modules;
    }
    if (hasMetadata) fallbackResult.metadata = metadata;
    return fallbackResult;
  }

  // Unclassified — could be a bare number, dimension, or unrecognized format
  return null;
}

// ── Keyword-Aware Fallback ─────────────────────────────────
// Handles combinations of known topology keywords not covered by built-in patterns.
// Only fires when at least one known keyword with digits is present.

const KEYWORD_TO_LEVEL: Record<string, TopologyLevel> = {
  DCCB: 'dccb', DCB: 'dccb', CB: 'dccb', ACB: 'dccb',
  INV: 'inverter', INVERTER: 'inverter',
  STR: 'string', STRING: 'string',
  MPPT: 'mppt',
  TR: 'station', TS: 'station', STATION: 'station',
  MOD: 'module', MODULE: 'module',
};

const KEYWORD_REGEX = /^([A-Za-z]+)(\d+)$/;

const LEVEL_HIERARCHY_DEPTH: Record<string, number> = {
  station: 1, inverter: 2, mppt: 3, dccb: 4, string: 5, module: 6,
};

function classifyByKeywordFallback(cleaned: string): LayoutClassification | null {
  const parts = cleaned.split(/[.-]/);
  if (parts.length < 2 || parts.length > 5) return null;

  const hierarchy: Partial<TopologyFields> = {};
  let hasKeyword = false;
  let deepestLevel: TopologyLevel = 'station';
  let deepestDepth = 0;
  let numericIndex = 0;

  for (const part of parts) {
    const kwMatch = part.match(KEYWORD_REGEX);
    if (kwMatch) {
      const prefix = kwMatch[1].toUpperCase();
      const level = KEYWORD_TO_LEVEL[prefix];
      if (level) {
        hasKeyword = true;
        (hierarchy as Record<string, string>)[level] = part;
        const depth = LEVEL_HIERARCHY_DEPTH[level] ?? 0;
        if (depth > deepestDepth) {
          deepestDepth = depth;
          deepestLevel = level;
        }
        continue;
      }
    }

    // Pure number — assign positionally (station, then mppt)
    if (/^\d+$/.test(part)) {
      if (numericIndex === 0 && !hierarchy.station) {
        hierarchy.station = part;
        const depth = LEVEL_HIERARCHY_DEPTH.station;
        if (depth > deepestDepth) { deepestDepth = depth; deepestLevel = 'station'; }
      } else if (numericIndex === 1 && !hierarchy.mppt) {
        hierarchy.mppt = part;
        const depth = LEVEL_HIERARCHY_DEPTH.mppt;
        if (depth > deepestDepth) { deepestDepth = depth; deepestLevel = 'mppt'; }
      }
      numericIndex++;
      continue;
    }

    // Non-keyword, non-numeric part — not a topology label
    return null;
  }

  if (!hasKeyword) return null;

  return {
    level: deepestLevel,
    value: cleaned,
    hierarchy,
  };
}

// ── Batch Classification ──────────────────────────────────

export interface ClassifiedEntity {
  id: string;
  text: string;
  normalizedText: string;
  cx: number;
  cy: number;
  width: number;
  height: number;
  rotation: number;
  fontSize: number;
  classification?: LayoutClassification;
  classificationOverride?: LayoutClassification;
  merged?: boolean;
}

let entityCounter = 0;

/**
 * Full pipeline: merge raw text items → normalize → classify.
 * Returns classified entities ready for SiteLayout.textEntities.
 */
export function classifyAllEntities(
  rawItems: RawTextItem[],
  customPatterns?: ClassificationPattern[],
): ClassifiedEntity[] {
  // Step 1: Merge adjacent fragments
  const merged = mergeAdjacentTextItems(rawItems);

  // Step 2: Normalize + classify each item
  const entities: ClassifiedEntity[] = [];

  for (const item of merged) {
    const text = item.str.trim();
    if (!text) continue;

    const normalized = normalizeLabel(text);
    if (!normalized) continue;

    const classification = classifyLabel(normalized, customPatterns) ?? undefined;

    entities.push({
      id: `le-${Date.now()}-${entityCounter++}`,
      text,
      normalizedText: normalized,
      cx: item.x + item.width / 2,
      cy: item.y + item.height / 2,
      width: item.width,
      height: item.height,
      rotation: item.rotation,
      fontSize: item.fontSize,
      classification,
      merged: item.str !== text ? true : undefined,
    });
  }

  return entities;
}

/**
 * Re-classify existing entities with new patterns (for ClassificationConfigModal).
 * Does NOT re-merge — only re-runs classification on existing normalizedText.
 */
export function reclassifyEntities(
  entities: ClassifiedEntity[],
  customPatterns?: ClassificationPattern[],
): ClassifiedEntity[] {
  return entities.map((entity) => {
    // Manual overrides take precedence over auto-classification
    if (entity.classificationOverride) {
      return { ...entity, classification: entity.classificationOverride };
    }
    return {
      ...entity,
      classification: classifyLabel(entity.normalizedText, customPatterns) ?? undefined,
    };
  });
}

/**
 * Get classification statistics for display.
 */
export function getClassificationStats(
  entities: ClassifiedEntity[],
): Record<TopologyLevel | 'unclassified', number> {
  const stats: Record<string, number> = {
    station: 0,
    inverter: 0,
    mppt: 0,
    dccb: 0,
    string: 0,
    module: 0,
    row: 0,
    zone: 0,
    infrastructure: 0,
    unknown: 0,
    unclassified: 0,
  };

  for (const entity of entities) {
    if (entity.classification) {
      stats[entity.classification.level] = (stats[entity.classification.level] || 0) + 1;
    } else {
      stats.unclassified++;
    }
  }

  return stats as Record<TopologyLevel | 'unclassified', number>;
}
